// Channel layout manager — renders multi-pane xterm.js layout from server layout tree

import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { Unicode11Addon } from '@xterm/addon-unicode11';
import { LayoutNode, ChannelMessage } from './websocket';
import { TERMINAL_FONT_FAMILY, TERMINAL_FONT_SIZE, TERMINAL_THEME } from './terminal';

interface ChannelTerminal {
  terminal: Terminal;
  fitAddon: FitAddon;
  unicode11: Unicode11Addon;
  element: HTMLDivElement;
  w: number;
  h: number;
}

export class ChannelManager {
  private container: HTMLElement;
  private channels: Map<string, ChannelTerminal> = new Map();
  private onResizeCallback?: (cols: number, rows: number) => void;
  private resizeObserver: ResizeObserver;
  private active = false;

  constructor(container: HTMLElement, onResize?: (cols: number, rows: number) => void) {
    this.container = container;
    this.onResizeCallback = onResize;

    this.resizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });
    this.resizeObserver.observe(this.container);
  }

  get isActive(): boolean {
    return this.active;
  }

  setLayout(tree: LayoutNode): void {
    this.active = true;

    // Collect slot names we'll need
    const needed = new Set<string>();
    this.collectSlots(tree, needed);

    // Dispose terminals no longer in layout
    for (const [name, ch] of this.channels) {
      if (!needed.has(name)) {
        ch.fitAddon.dispose();
        ch.terminal.dispose();
        ch.unicode11.dispose();
        this.channels.delete(name);
      }
    }

    // Clear container and rebuild DOM
    this.container.innerHTML = '';
    this.buildDOM(tree, this.container);

    // Open terminals into their containers and fit them
    for (const [, ch] of this.channels) {
      if (!ch.element.querySelector('.xterm')) {
        ch.terminal.open(ch.element);
      }
      try { ch.fitAddon.fit(); } catch (_) { /* ignore during init */ }
    }

    this.handleResize();
  }

  updateChannel(msg: ChannelMessage): void {
    let ch = this.channels.get(msg.name);
    if (!ch) return;

    // Resize terminal if server dimensions changed
    if (msg.w !== ch.w || msg.h !== ch.h) {
      ch.w = msg.w;
      ch.h = msg.h;
      try {
        ch.terminal.resize(msg.w, msg.h);
      } catch (_) {
        // Ignore resize errors during init
      }
    }

    // Write data at cursor home position (overwrite in place)
    ch.terminal.write('\x1b[H' + msg.data);
  }

  triggerResize(): void {
    // Defer to next frame to ensure DOM is fully laid out
    requestAnimationFrame(() => this.handleResize());
  }

  deactivate(): void {
    this.active = false;
  }

  dispose(): void {
    this.resizeObserver.disconnect();
    for (const [, ch] of this.channels) {
      ch.fitAddon.dispose();
      ch.terminal.dispose();
      ch.unicode11.dispose();
    }
    this.channels.clear();
    this.container.innerHTML = '';
    this.active = false;
  }

  private collectSlots(node: LayoutNode, out: Set<string>): void {
    if (typeof node === 'string') {
      out.add(node);
    } else {
      this.collectSlots(node.a, out);
      this.collectSlots(node.b, out);
    }
  }

  private buildDOM(node: LayoutNode, parent: HTMLElement): void {
    if (typeof node === 'string') {
      // Leaf node — create a pane with a terminal
      const pane = document.createElement('div');
      pane.className = 'channel-pane';
      pane.dataset.channel = node;

      const termDiv = document.createElement('div');
      termDiv.className = 'channel-terminal';
      pane.appendChild(termDiv);
      parent.appendChild(pane);

      // Reuse existing terminal or create new one
      let ch = this.channels.get(node);
      if (!ch) {
        const terminal = new Terminal({
          fontFamily: TERMINAL_FONT_FAMILY,
          fontSize: TERMINAL_FONT_SIZE,
          lineHeight: 1.2,
          letterSpacing: 0,
          allowProposedApi: true,
          scrollback: 0,
          disableStdin: true,
          cursorBlink: false,
          cursorInactiveStyle: 'none',
          theme: TERMINAL_THEME,
        });
        const fitAddon = new FitAddon();
        terminal.loadAddon(fitAddon);
        const unicode11 = new Unicode11Addon();
        terminal.loadAddon(unicode11);
        terminal.unicode.activeVersion = '11';
        ch = { terminal, fitAddon, unicode11, element: termDiv, w: 0, h: 0 };
        this.channels.set(node, ch);
      } else {
        ch.element = termDiv;
      }
    } else {
      // Split node
      const split = document.createElement('div');
      split.className = 'channel-split';
      split.style.flexDirection = node.split === 'h' ? 'row' : 'column';
      parent.appendChild(split);

      // First child
      const wrapA = document.createElement('div');
      wrapA.className = 'channel-child';
      const pct = (node.ratio * 100).toFixed(4);
      if (node.split === 'h') {
        wrapA.style.width = pct + '%';
        wrapA.style.height = '100%';
      } else {
        wrapA.style.height = pct + '%';
        wrapA.style.width = '100%';
      }
      split.appendChild(wrapA);
      this.buildDOM(node.a, wrapA);

      // Second child
      const wrapB = document.createElement('div');
      wrapB.className = 'channel-child';
      const pctB = ((1 - node.ratio) * 100).toFixed(4);
      if (node.split === 'h') {
        wrapB.style.width = pctB + '%';
        wrapB.style.height = '100%';
      } else {
        wrapB.style.height = pctB + '%';
        wrapB.style.width = '100%';
      }
      split.appendChild(wrapB);
      this.buildDOM(node.b, wrapB);
    }
  }

  private handleResize(): void {
    if (!this.active) return;

    const rect = this.container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    // Fit all channel terminals to their containers
    for (const [, ch] of this.channels) {
      try { ch.fitAddon.fit(); } catch (_) { /* ignore */ }
    }

    // Compute total grid size from one terminal's cell metrics
    // After fit(), we can derive cell size from a pane's pixel width / cols
    for (const [, ch] of this.channels) {
      if (ch.terminal.cols > 0 && ch.terminal.rows > 0) {
        const paneRect = ch.element.getBoundingClientRect();
        if (paneRect.width > 0 && paneRect.height > 0) {
          const cellWidth = paneRect.width / ch.terminal.cols;
          const cellHeight = paneRect.height / ch.terminal.rows;
          const totalCols = Math.floor(rect.width / cellWidth);
          const totalRows = Math.floor(rect.height / cellHeight);
          if (totalCols > 0 && totalRows > 0 && this.onResizeCallback) {
            this.onResizeCallback(totalCols, totalRows);
          }
          break;
        }
      }
    }
  }
}
